/**
 * ポータルのウィジェット定義（S-03 / F-PTL-01・02）。担当: G4（web-foundation の差し込み口を実データ化）
 *
 * kintone 版の組織区分別ホーム（`apps/portal/index.js:87-100`）を再現する。
 *   - 漁協（fishHome）      ：今日の定時連絡（日付ページング・済／未送信・入力）＋ 海域図（自組織の出港中実施船）
 *   - 基金・水産庁・沖縄県（countryHome）：指定日の出港船（全域／尖閣諸島周辺）＋ 海域図 ＋ 事業実施者別隻数
 * 日付・海域選択は URL クエリ（?date=&area=）で共有し、ウィジェット間で同期する。
 */
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Anchor, CalendarClock, Map as MapIcon, Users } from 'lucide-react';
import type { AuthUser, OrganizationType } from '@/auth/types';
import { usePortalSummaryQuery } from './api';
import { DailyDeparturesWidget, DailyMapWidget, OperatorCountsWidget, TodayFixedReportsWidget } from './PortalWidgets';

export interface PortalWidgetContext {
  year: number;
  user: AuthUser;
  /** 年度付きパスを作る */
  yearPath: (sub?: string) => string;
}

export interface PortalWidgetDef {
  id: string;
  title: string;
  icon: LucideIcon;
  /** 'wide' = 2 列分 / 'narrow' = 1 列分 */
  span: 'wide' | 'narrow';
  roles?: readonly OrganizationType[];
  /** 見出し右のリンク（一覧画面など） */
  link?: { label: string; path: string };
  render: (ctx: PortalWidgetContext) => ReactNode;
}

const COUNTRY_ROLES: readonly OrganizationType[] = ['基金', '水産庁', '沖縄県'];

export const PORTAL_WIDGETS: PortalWidgetDef[] = [
  {
    id: 'today-fixed-reports',
    title: '今日の定時連絡',
    icon: CalendarClock,
    span: 'wide',
    roles: ['漁協'],
    link: { label: '定時連絡の一覧', path: 'fixed-reports' },
    render: (ctx) => <TodayFixedReportsWidget {...ctx} />,
  },
  {
    id: 'daily-departures',
    title: '指定日の出港船',
    icon: Anchor,
    span: 'wide',
    roles: COUNTRY_ROLES,
    link: { label: '事前連絡の一覧', path: 'advance-notices' },
    render: (ctx) => <DailyDeparturesWidget {...ctx} />,
  },
  {
    id: 'daily-map',
    title: '海域図（出港中の実施船）',
    icon: MapIcon,
    span: 'narrow',
    link: { label: '海域図', path: 'sea-map' },
    render: (ctx) => <DailyMapWidget {...ctx} />,
  },
  {
    id: 'operator-counts',
    title: '事業実施者別隻数',
    icon: Users,
    span: 'narrow',
    roles: COUNTRY_ROLES,
    render: (ctx) => <OperatorCountsWidget {...ctx} />,
  },
];

/**
 * 計画日数・実績日数・消化率（kintone 版サイドバー上部。L-34）。
 * GET /api/portal/summary（漁協は自組織、基金・国・県は全事業実施者）。
 */
export interface PortalSummary {
  plannedDays: number | null;
  actualDays: number | null;
}
export function usePortalSummary(year: number): PortalSummary | undefined {
  const { data } = usePortalSummaryQuery(year);
  return data ? { plannedDays: data.planDays, actualDays: data.actualDays } : undefined;
}
